/**
 * What: Shows whether this Shopify shop can create TestFeed setup packets.
 * Transport / data-flow: Pure Polaris component fed by `/api/testfeed-connection-status`.
 *
 */
import { useState } from 'react'
import type { TestFeedConnectionStatus } from '../types'
import { launchAdmissionCopy, productLanguage, shopifyComponentCopy } from '@testfeed/product-language'
import { ActionButton, type ActionButtonLifecycle } from './ActionButton'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../shopifyUiTracking'

type TestFeedReadinessCardProps = {
  readiness: TestFeedConnectionStatus | null
  isLoading: boolean
  isConnecting: boolean
  isDisconnecting: boolean
  isDisconnectUnconfirmed?: boolean
  connectionError: string | null
  planSelectionUrl?: string
  onConnect: () => void
  onDisconnect: () => void
  onRetryCleanup?: () => void
  /** Settings supplies its page lifecycle; the bootstrap shell keeps its existing connect state. */
  lifecycle?: ActionButtonLifecycle<'connect' | 'disconnect' | 'retryCleanup'>
}

type ReadinessCopy = {
  title: string
  detail: string
  badgeTone: 'info' | 'attention' | 'success' | 'critical'
  badgeText: string
  actionText?: string
  canConnect: boolean
}

export function resolveTestFeedReadinessPresentation(
  readiness: TestFeedConnectionStatus | null,
  isLoading: boolean,
): ReadinessCopy {
  const disconnectedTitle = productLanguage.shopify.studyBuilder.readinessBanners.disconnected.title

  if (isLoading) {
    return {
      title: shopifyComponentCopy.testFeedReadinessCard.checkingTestfeedConnection001,
      detail: shopifyComponentCopy.testFeedReadinessCard.loadingWorkspaceStatus002,
      badgeTone: 'info' as const,
      badgeText: 'Checking',
      actionText: 'Connect TestFeed',
      canConnect: false,
    }
  }

  if (readiness?.code === 'READY') {
    return {
      title: shopifyComponentCopy.testFeedReadinessCard.connectedToTestfeed004,
      detail: readiness.message,
      badgeTone: 'success' as const,
      badgeText: 'Connected',
      canConnect: false,
    }
  }

  if (!readiness) {
    return {
      title: shopifyComponentCopy.testFeedReadinessCard.configurationProblem006,
      detail: shopifyComponentCopy.shopifyApiErrors.connectionStatusUnavailable022,
      badgeTone: 'critical' as const,
      badgeText: 'Unavailable',
      canConnect: false,
    }
  }

  if (readiness.code === 'SHOPIFY_CONNECTION_DISCONNECTED') {
    return {
      title: disconnectedTitle,
      detail: readiness.message,
      badgeTone: 'attention' as const,
      badgeText: 'Disconnected',
      actionText: 'Reconnect TestFeed',
      canConnect: readiness.cleanup === null || readiness.cleanup?.status === 'completed',
    }
  }

  if (readiness.code === 'SHOPIFY_CONNECTION_UNINSTALLED') {
    return {
      title: disconnectedTitle,
      detail: readiness.message,
      badgeTone: 'attention' as const,
      badgeText: 'Uninstalled',
      actionText: 'Reconnect TestFeed',
      canConnect: true,
    }
  }

  if (readiness.code === 'DEFAULT_PROJECT_NOT_CONFIGURED') {
    return {
      title: shopifyComponentCopy.testFeedReadinessCard.chooseDestinationProject005,
      detail: readiness.message,
      badgeTone: 'attention' as const,
      badgeText: 'Project needed',
      actionText: 'Open TestFeed',
      canConnect: true,
    }
  }

  // Resolve ambiguous account ownership through the existing top-level handoff.
  if (readiness.code === 'SHOPIFY_CONNECTION_SELECTION_REQUIRED') {
    return {
      title: launchAdmissionCopy.chooseAccount,
      detail: readiness.message,
      badgeTone: 'attention',
      badgeText: launchAdmissionCopy.accountNeeded,
      actionText: launchAdmissionCopy.chooseAccount,
      canConnect: true,
    }
  }

  if (readiness.code === 'SHOPIFY_CONNECTION_NOT_FOUND') {
    const notConnectedCopy = productLanguage.shopify.studyBuilder.readinessBanners.notConnected
    return {
      title: notConnectedCopy.title,
      detail: readiness.message,
      badgeTone: 'attention' as const,
      badgeText: 'Not connected',
      actionText: notConnectedCopy.action,
      canConnect: true,
    }
  }

  return {
    title: shopifyComponentCopy.testFeedReadinessCard.configurationProblem006,
    detail: readiness.message,
    badgeTone: 'critical' as const,
    badgeText: 'Blocked',
    actionText: 'Connect TestFeed',
    canConnect: false,
  }
}

/** Renders the current TestFeed account and project readiness next to the connection action. */
export function TestFeedReadinessCard({
  readiness,
  isLoading,
  isConnecting,
  isDisconnecting,
  isDisconnectUnconfirmed = false,
  connectionError,
  planSelectionUrl,
  onConnect,
  onDisconnect,
  onRetryCleanup,
  lifecycle,
}: TestFeedReadinessCardProps) {
  const shopify = useAppBridge()
  const [isConfirmingDisconnect, setIsConfirmingDisconnect] = useState(false)
  const copy = resolveTestFeedReadinessPresentation(readiness, isLoading || isDisconnectUnconfirmed)
  const buttonLifecycle = lifecycle ?? {
    button: (action: 'connect' | 'disconnect' | 'retryCleanup') => ({
      loading: action === 'connect' ? isConnecting : isDisconnecting,
      disabled: isLoading || isConnecting || isDisconnecting,
    }),
  }
  const isConnected = readiness?.code === 'READY' && !isDisconnectUnconfirmed
  const shouldShowDisconnect = readiness?.connectionStatus === 'active' && Boolean(readiness?.connectionId)
  const shopLabel = readiness?.externalAccountId
  const cleanup = readiness?.cleanup
  const words = shopifyComponentCopy.testFeedReadinessCard
  const disconnectLabel = isDisconnectUnconfirmed ? words.retryDisconnect : words.disconnectTestfeed007
  let cleanupMessage: string | null = null
  if (readiness?.connectionStatus === 'disabled') {
    cleanupMessage = words.cleanupUnconfirmed
    if (cleanup?.status === 'pending') cleanupMessage = words.cleanupPending
    if (cleanup?.status === 'failed')
      cleanupMessage = cleanup.canRetry ? words.cleanupFailed : words.cleanupNeedsSupport
    if (cleanup?.status === 'completed') cleanupMessage = words.cleanupCompleted
  }
  const showRetryCleanup = readiness?.connectionStatus === 'disabled' && cleanup?.canRetry && onRetryCleanup
  const handleConnectClick = () => onConnect()
  const handleDisconnectClick = () => {
    trackShopifyAction(shopify, 'confirm-disconnect', () => setIsConfirmingDisconnect(true))
  }
  const handleCancelDisconnect = () => {
    trackShopifyAction(shopify, 'cancel-disconnect', () => setIsConfirmingDisconnect(false))
  }
  const handleConfirmDisconnect = () => {
    onDisconnect()
  }

  return (
    <s-section padding="base">
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
          <s-heading>{copy.title}</s-heading>
          <s-text color="subdued">{copy.badgeText}</s-text>
        </s-stack>
        <s-text>{copy.detail}</s-text>
        {shopLabel && <s-text color="subdued">{shopLabel}</s-text>}
        <s-stack direction="inline" gap="small-200">
          {!isConnected && (
            <ActionButton
              lifecycle={buttonLifecycle}
              action="connect"
              onClick={handleConnectClick}
              disabled={!copy.canConnect}
            >
              {copy.actionText}
            </ActionButton>
          )}
          {shouldShowDisconnect && !isConfirmingDisconnect && (
            <s-button
              tone="critical"
              onClick={handleDisconnectClick}
              disabled={buttonLifecycle.button('disconnect').disabled || isLoading}
            >
              {disconnectLabel}
            </s-button>
          )}
          <s-button
            href={planSelectionUrl}
            target="_top"
            disabled={!planSelectionUrl}
            variant="tertiary"
            onClick={() => trackShopifyAction(shopify, 'choose-plan', () => undefined)}
          >
            {shopifyComponentCopy.testFeedReadinessCard.managePlan008}
          </s-button>
        </s-stack>
        {cleanupMessage && <s-text>{cleanupMessage}</s-text>}
        {showRetryCleanup && (
          <ActionButton lifecycle={buttonLifecycle} action="retryCleanup" onClick={onRetryCleanup}>
            {words.retryCleanup}
          </ActionButton>
        )}
        {connectionError && <s-text tone="critical">{connectionError}</s-text>}
        {isConfirmingDisconnect && shouldShowDisconnect && (
          <>
            <s-divider />
            <s-stack direction="block" gap="base">
              <s-stack direction="block" gap="small-100">
                <s-heading>{shopifyComponentCopy.testFeedReadinessCard.disconnectThisShopifyStoreFrom010}</s-heading>
                <s-text>{shopifyComponentCopy.testFeedReadinessCard.thisDisconnectsThisShopifyStore011}</s-text>
                <s-text color="subdued">
                  {shopifyComponentCopy.testFeedReadinessCard.theShopifyAppStaysInstalled012}
                </s-text>
                <s-text color="subdued">
                  {shopifyComponentCopy.testFeedReadinessCard.disconnectingAlsoFreesThisStore017}
                </s-text>
                <s-text color="subdued">{shopifyComponentCopy.testFeedReadinessCard.newSetupSyncAndRun013}</s-text>
                <s-text>{words.storeDataCleanup}</s-text>
                <s-text color="subdued">
                  {shopifyComponentCopy.testFeedReadinessCard.historicalTestfeedStudiesAndResults014}
                </s-text>
              </s-stack>
              <s-stack direction="inline" gap="small-200">
                <s-button onClick={handleCancelDisconnect} disabled={buttonLifecycle.button('disconnect').loading}>
                  {shopifyComponentCopy.testFeedReadinessCard.keepConnected015}
                </s-button>
                <ActionButton
                  lifecycle={buttonLifecycle}
                  action="disconnect"
                  tone="critical"
                  onClick={handleConfirmDisconnect}
                  disabled={isLoading || !readiness?.connectionActivatedAt}
                >
                  {shopifyComponentCopy.testFeedReadinessCard.confirmDisconnect016}
                </ActionButton>
              </s-stack>
            </s-stack>
          </>
        )}
      </s-stack>
    </s-section>
  )
}
